/**
 * The `Character` document and its derived cache
 * (architecture §5.2; `docs/research/character-sheet-model.md` §3–§4).
 *
 * Design rules, unchanged from the research:
 *  1. Store inputs, cache outputs. `derived` is recomputable from the rest and the
 *     server is its only writer.
 *  2. Every rules entity is a stable English id; display text comes from the catalogs.
 *  3. Every number that changes carries provenance, so the sheet and the Guia can both
 *     say "CA 18 = cota de malha 16 + escudo 2" from one array.
 *  4. Overrides, not edits — and in v1 the only writers of `overrides` are the pack
 *     (pregens) and the quick build. There is no player-editable override, because
 *     there is no fudge surface anywhere (§3.8).
 *  5. Ephemeral combat state (grid position, initiative, turn resources) lives in the
 *     Encounter, never here.
 */
import { z } from 'zod';
import { AbilityId, Alignment, ConditionId, DamageType, DiceExpr, Locale, Modifier, ProficiencyLevel, Recharge, SkillId, Size, } from './primitives.js';
/* ------------------------------------------------------------- identity */
export const ClassLevel = z.object({
    /** 'class:fighter' */
    classId: z.string(),
    /** 'subclass:champion'; chosen at 3rd level in the 2024 rules. */
    subclassId: z.string().nullable(),
    level: z.number().int().min(1).max(20),
    hitDie: z.number().int(),
    hitDiceSpent: z.number().int().min(0),
    weaponMasteryCount: z.number().int().min(0).default(0),
});
export const AbilityScore = z.object({
    base: z.number().int().min(1).max(30),
    bonuses: z.array(Modifier).default([]),
    /** Headband-of-Intellect style "set to 19". */
    override: z.number().int().optional(),
    max: z.number().int().default(20),
});
/* ---------------------------------------------------------------- state */
export const CharacterCondition = z.object({
    id: ConditionId,
    source: z.string().optional(),
    appliedAtRound: z.number().int().optional(),
    expiresAtRound: z.number().int().optional(),
    saveToEnd: z
        .object({
        ability: AbilityId,
        dc: z.number().int(),
        timing: z.enum(['startOfTurn', 'endOfTurn']),
    })
        .optional(),
    notes: z.string().optional(),
});
export const Concentration = z.object({
    spellId: z.string(),
    spellLevel: z.number().int(),
    startedAt: z.string().datetime(),
    encounterId: z.string().optional(),
    expiresAtRound: z.number().int().optional(),
    targets: z.array(z.string()).default([]),
});
export const Resource = z.object({
    /** 'resource:second_wind', 'resource:channel_divinity' */
    id: z.string(),
    name: z.string().optional(),
    current: z.number().int().min(0),
    max: z.number().int().min(0),
    recharge: Recharge,
    die: z.string().optional(),
    source: z.string(),
});
export const SpellSlotRow = z.object({
    total: z.number().int().min(0),
    expended: z.number().int().min(0),
});
export const PreparedSpell = z.object({
    spellId: z.string(),
    /** 0 = cantrip ("Truque"). */
    level: z.number().int().min(0).max(9),
    source: z.string(),
    alwaysPrepared: z.boolean().default(false),
    usesPerLongRest: z.number().int().optional(),
    usesLeft: z.number().int().optional(),
    castingAbility: AbilityId.optional(),
    notes: z.string().optional(),
});
export const Spellcasting = z.object({
    ability: AbilityId.nullable(),
    casterLevel: z.number().int().min(0).default(0),
    slots: z
        .record(z.enum(['1', '2', '3', '4', '5', '6', '7', '8', '9']), SpellSlotRow)
        .default({}),
    pactSlots: z
        .object({
        level: z.number().int(),
        total: z.number().int(),
        expended: z.number().int(),
    })
        .optional(),
    preparedMax: z.number().int().default(0),
    cantripsMax: z.number().int().default(0),
    prepared: z.array(PreparedSpell).default([]),
    spellbook: z.array(z.string()).default([]),
    focus: z.string().optional(),
    ritualCasting: z.boolean().default(false),
});
export const InventoryItem = z.object({
    instanceId: z.string(),
    itemId: z.string().nullable(),
    name: z.string().optional(),
    quantity: z.number().int().min(0).default(1),
    /** Per unit, in pounds — the ruleset unit; pt-BR renders kilograms. */
    weightLb: z.number().min(0).default(0),
    /** Per unit, in copper — avoids float gold. */
    valueCp: z.number().int().min(0).default(0),
    location: z.enum(['equipped', 'carried', 'stored', 'mount', 'bastion']).default('carried'),
    slot: z
        .enum([
        'armor',
        'shield',
        'mainHand',
        'offHand',
        'twoHand',
        'head',
        'neck',
        'ring',
        'cloak',
        'boots',
        'belt',
        'gloves',
        'ammo',
        'focus',
    ])
        .optional(),
    attuned: z.boolean().default(false),
    requiresAttunement: z.boolean().default(false),
    charges: z
        .object({
        current: z.number().int(),
        max: z.number().int(),
        recharge: Recharge,
        rechargeDice: z.string().optional(),
    })
        .optional(),
    container: z.string().optional(),
    identified: z.boolean().default(true),
    notes: z.string().optional(),
    weapon: z
        .object({
        category: z.enum(['simple', 'martial']),
        damage: DiceExpr,
        damageType: DamageType,
        properties: z.array(z.string()),
        mastery: z.string().optional(),
        rangeFt: z.tuple([z.number(), z.number()]).optional(),
    })
        .optional(),
    armor: z
        .object({
        category: z.enum(['light', 'medium', 'heavy', 'shield']),
        baseAc: z.number().int(),
        strRequirement: z.number().int().optional(),
        stealthDisadvantage: z.boolean().default(false),
    })
        .optional(),
});
/** Keyed cp/sp/ep/gp/pp in code and data: pt-BR "PP" means silver (§9.3). */
export const Coins = z.object({
    cp: z.number().int().min(0),
    sp: z.number().int().min(0),
    ep: z.number().int().min(0),
    gp: z.number().int().min(0),
    pp: z.number().int().min(0),
});
export const Feature = z.object({
    id: z.string(),
    source: z.string(),
    choices: z.record(z.string(), z.unknown()).default({}),
    customText: z.string().optional(),
});
export const JournalEntry = z.object({
    id: z.string(),
    sessionId: z.string(),
    at: z.string().datetime(),
    author: z.enum(['player', 'gm', 'system']),
    kind: z.enum(['note', 'recap', 'quest', 'ally', 'treasure', 'levelUp', 'death']),
    text: z.string(),
    refs: z.array(z.string()).default([]),
});
/* -------------------------------------------------------- derived cache */
export const DerivedAttack = z.object({
    id: z.string(),
    name: z.string(),
    kind: z.enum(['melee', 'ranged', 'thrown', 'cantrip']),
    toHit: z.number().int().nullable(),
    saveDc: z.number().int().nullable(),
    saveAbility: AbilityId.nullable(),
    damage: z.array(z.object({ dice: DiceExpr, type: DamageType })),
    rangeFt: z.tuple([z.number(), z.number()]).nullable(),
    properties: z.array(z.string()),
    mastery: z.string().nullable(),
    sourceInstanceId: z.string().nullable(),
});
export const Derived = z.object({
    /** e.g. 'srd-5.2.1+everwake.2026.10' — the rules a replay must use (§5.1). */
    ruleVersion: z.string(),
    computedAt: z.string().datetime(),
    level: z.number().int(),
    proficiencyBonus: z.number().int(),
    abilities: z.record(AbilityId, z.object({ score: z.number().int(), mod: z.number().int() })),
    saves: z.record(AbilityId, z.object({
        bonus: z.number().int(),
        proficient: z.boolean(),
        parts: z.array(Modifier),
    })),
    skills: z.record(SkillId, z.object({
        bonus: z.number().int(),
        proficiency: ProficiencyLevel,
        parts: z.array(Modifier),
    })),
    passive: z.object({
        perception: z.number().int(),
        investigation: z.number().int(),
        insight: z.number().int(),
    }),
    initiative: z.number().int(),
    armorClass: z.object({
        value: z.number().int(),
        parts: z.array(Modifier),
        formula: z.string(),
    }),
    hpMax: z.number().int(),
    speed: z.record(z.enum(['walk', 'fly', 'swim', 'climb', 'burrow']), z.number().int()),
    spellSaveDc: z.number().int().nullable(),
    spellAttackBonus: z.number().int().nullable(),
    attacks: z.array(DerivedAttack),
    carrying: z.object({
        loadLb: z.number(),
        capacityLb: z.number(),
        pushDragLiftLb: z.number(),
        encumbered: z.boolean(),
    }),
    hitDice: z.array(z.object({
        die: z.number().int(),
        total: z.number().int(),
        spent: z.number().int(),
    })),
    /** Exhaustion penalty (−2 × level), applied by the roller to every d20 test. */
    d20Penalty: z.number().int(),
});
/* ------------------------------------------------------------- document */
export const LifeState = z.enum(['alive', 'unconscious', 'stable', 'dead']);
/** Stored so pt-BR narration inflects ("a guerreira anã"). */
export const GrammaticalGender = z.enum(['f', 'm', 'n']);
export const Character = z.object({
    id: z.string(),
    schemaVersion: z.literal(1),
    /** Everwake is 2024-rules only; there is no `dnd2014` flag in v1 (§5.1). */
    rulesetId: z.literal('srd-5.2.1'),
    ownerUserId: z.string(),
    campaignId: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /** The player's language, not the data language. */
    locale: Locale,
    identity: z.object({
        name: z.string().min(1),
        speciesId: z.string(),
        lineageId: z.string().optional(),
        backgroundId: z.string(),
        /** v1 enforces exactly one entry: multiclass is disabled (§5.6). */
        classes: z.array(ClassLevel).min(1),
        /** Display only; XP never triggers a level (§5.6). */
        xp: z.number().int().min(0).default(0),
        alignment: Alignment.optional(),
        size: Size,
        appearance: z.string().default(''),
        backstory: z.string().default(''),
        personality: z
            .object({
            traits: z.array(z.string()),
            ideals: z.array(z.string()),
            bonds: z.array(z.string()),
            flaws: z.array(z.string()),
        })
            .optional(),
        portraitAssetId: z.string().optional(),
        gender: GrammaticalGender,
        pronouns: z.string().optional(),
        /** ~300-token narrator-facing summary; prompt layer C carries it (§3.2). */
        aiDigest: z.string().optional(),
    }),
    abilities: z.record(AbilityId, AbilityScore),
    saveProficiencies: z.array(AbilityId).default([]),
    skills: z
        .record(SkillId, z.object({ proficiency: ProficiencyLevel, source: z.string().optional() }))
        .default({}),
    proficiencies: z.object({
        armorTraining: z.object({
            light: z.boolean(),
            medium: z.boolean(),
            heavy: z.boolean(),
            shields: z.boolean(),
        }),
        weapons: z.array(z.string()).default([]),
        tools: z
            .array(z.object({ toolId: z.string(), proficiency: ProficiencyLevel }))
            .default([]),
        languages: z.array(z.string()).default([]),
    }),
    weaponMasteries: z.array(z.string()).default([]),
    features: z.object({
        classFeatures: z.array(Feature).default([]),
        speciesTraits: z.array(Feature).default([]),
        feats: z.array(Feature).default([]),
        backgroundFeature: Feature.optional(),
    }),
    combat: z.object({
        hp: z.object({
            current: z.number().int(),
            temp: z.number().int().min(0).default(0),
            maxOverride: z.number().int().optional(),
            maxBonuses: z.array(Modifier).default([]),
        }),
        deathSaves: z.object({
            successes: z.number().int().min(0).max(3),
            failures: z.number().int().min(0).max(3),
        }),
        /** The AI-Master is allowed to end in 'dead' (§3.8). */
        lifeState: LifeState.default('alive'),
        conditions: z.array(CharacterCondition).default([]),
        exhaustion: z.number().int().min(0).max(6).default(0),
        concentration: Concentration.nullable().default(null),
        heroicInspiration: z.object({
            has: z.boolean(),
            source: z.string().optional(),
            grantedAt: z.string().datetime().optional(),
        }),
        activeEffects: z
            .array(z.object({
            id: z.string(),
            source: z.string(),
            modifiers: z.array(Modifier.extend({ target: z.string() })),
            expiresAtRound: z.number().int().optional(),
            expiresAt: z.string().datetime().optional(),
        }))
            .default([]),
        speedOverride: z.record(z.string(), z.number().int()).optional(),
        lastShortRestAt: z.string().datetime().optional(),
        lastLongRestAt: z.string().datetime().optional(),
    }),
    resources: z.array(Resource).default([]),
    spellcasting: Spellcasting.optional(),
    inventory: z.object({
        items: z.array(InventoryItem).default([]),
        coins: Coins,
        attunement: z.object({
            max: z.number().int().default(3),
            attunedInstanceIds: z.array(z.string()).max(3),
        }),
    }),
    notes: z.object({
        general: z.string().default(''),
        allies: z.string().default(''),
        quests: z.string().default(''),
        treasures: z.string().default(''),
        journal: z.array(JournalEntry).default([]),
    }),
    /** 'armorClass', 'initiative', 'skill:stealth', 'save:dex', 'spellSaveDc' … */
    overrides: z.record(z.string(), z.number().int()).default({}),
    derived: Derived.optional(),
    meta: z.object({
        importedFrom: z
            .object({
            format: z.literal('ficha-dnd55'),
            version: z.number(),
            sheetId: z.string().optional(),
            importedAt: z.string().datetime(),
        })
            .optional(),
        /** 'subclass', 'feat', 'mastery', 'spells' — blocks play until resolved. */
        pendingLevelUpChoices: z.array(z.string()).default([]),
        tags: z.array(z.string()).default([]),
    }),
});
/**
 * The v1 validator: everything `Character` enforces plus "multiclass is disabled"
 * (§5.6). `Character.classes[]` keeps its shape for the M2 builder and the sheet
 * importer, but a v1 document with two classes is invalid.
 */
export const CharacterV1 = Character.superRefine((doc, ctx) => {
    if (doc.identity.classes.length !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['identity', 'classes'],
            message: 'Multiclass is disabled in v1: identity.classes must hold exactly one entry.',
        });
    }
});
/** PB = 2 + floor((level − 1) / 4). */
export function proficiencyBonus(level) {
    return 2 + Math.floor((Math.min(Math.max(level, 1), 20) - 1) / 4);
}
/** Ability modifier = floor((score − 10) / 2), rounding toward −∞. */
export function abilityModifier(score) {
    return Math.floor((score - 10) / 2);
}
//# sourceMappingURL=character.js.map